import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { RequireAuth } from "@/components/RequireAuth";

const STATUS = {
  open: "bg-[#F59E0B]/15 text-[#F59E0B]",
  won: "bg-[#10B981]/15 text-[#10B981]",
  lost: "bg-[#EF4444]/15 text-[#EF4444]",
  void: "bg-slate-500/15 text-slate-300",
};

const LEG = { open: "text-slate-400", won: "text-[#10B981]", lost: "text-[#EF4444]", void: "text-slate-400" };

export const BetCard = ({ bet }) => {
  const { fmt } = useCurrency();
  return (
    <div data-testid={`bet-card-${bet.id}`} className="rise-in overflow-hidden rounded-lg border border-[#1E293B] bg-[#121824]">
      <div className="flex items-center justify-between border-b border-[#1E293B] bg-[#0E131F] px-4 py-2.5">
        <span className="font-display text-sm font-bold uppercase tracking-wide">
          {bet.type === "multi" ? `Multi · ${bet.legs.length} legs` : "Single"}
          {bet.boost > 0 && <span className="ml-2 text-[#10B981]">+{Math.round(bet.boost * 100)}% boost</span>}
        </span>
        <span data-testid={`bet-status-${bet.id}`} className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${STATUS[bet.status]}`}>
          {bet.status}
        </span>
      </div>
      <div className="divide-y divide-[#1E293B]">
        {bet.legs.map((l) => (
          <div key={l.event_id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
            <div className="min-w-0">
              <div className="font-semibold">{l.pick_label}</div>
              <div className="truncate text-xs text-slate-400">
                {l.subtitle || `${l.home} v ${l.away} · ${l.league}`} {l.final_score && `· ${l.kind ? l.final_score : "FT " + l.final_score}`}
              </div>
            </div>
            <div className="text-right">
              <div className="font-odds font-bold">{l.odds.toFixed(2)}</div>
              <div className={`text-[10px] font-bold uppercase ${LEG[l.result]}`}>{l.result}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 border-t border-[#1E293B] px-4 py-3 text-xs">
        <div><div className="text-slate-500">Stake</div><div className="font-odds font-bold">{fmt(bet.stake_usd)}</div></div>
        <div><div className="text-slate-500">Odds</div><div className="font-odds font-bold">{bet.total_odds.toFixed(2)}</div></div>
        <div className="text-right">
          <div className="text-slate-500">{bet.status === "open" ? "Potential" : "Returned"}</div>
          <div className="font-odds font-bold text-[#10B981]">{fmt(bet.status === "open" ? bet.potential_usd : bet.payout_usd)}</div>
        </div>
      </div>
      <div className="px-4 pb-2 text-[10px] text-slate-500">{new Date(bet.created_at).toLocaleString()}</div>
    </div>
  );
};

function BetsList() {
  const [tab, setTab] = useState("open");
  const { data } = useQuery({
    queryKey: ["bets", tab],
    queryFn: () => api.get("/bets", { params: { status: tab } }).then((r) => r.data),
    refetchInterval: 30000,
  });
  return (
    <div className="space-y-5">
      <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">My Bets</h1>
      <div className="flex gap-1">
        {["open", "settled"].map((t) => (
          <button
            key={t}
            data-testid={`my-bets-tab-${t}`}
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${tab === t ? "bg-[#10B981] text-[#0B0E14]" : "bg-[#121824] text-slate-300"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {data && data.length === 0 && (
        <div data-testid="my-bets-empty" className="rounded-lg border border-dashed border-[#1E293B] p-10 text-center text-sm text-slate-400">
          No {tab} bets yet.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {(data || []).map((b) => (
          <BetCard key={b.id} bet={b} />
        ))}
      </div>
      <p className="text-xs text-slate-500">Bets settle automatically from official results, usually within a few minutes of the final whistle.</p>
    </div>
  );
}

export default function MyBets() {
  return (
    <RequireAuth>
      <BetsList />
    </RequireAuth>
  );
}
